import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';

export const COLORS = {
  primary: '#2563EB',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  pink: '#EC4899',
  slate: '#64748B',
  border: '#E2E8F0',
  bgAlt: '#F8FAFC',
  vip: '#D97706',
  line: '#06C755',
  shopee: '#EE4D2D',
  storefront: '#2563EB',
  outlet: '#7C3AED',
};

export const formatNumber = (n: number | null | undefined, digits = 0): string => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const formatCurrency = (n: number | null | undefined): string => {
  if (n === null || n === undefined || isNaN(n)) return 'NT$ —';
  return `NT$ ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

export const formatPct = (n: number | null | undefined, digits = 1): string => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return `${Number(n).toFixed(digits)}%`;
};

export const formatMonth = (s: string): string => {
  if (!s) return '';
  try {
    const d = new Date(s);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  } catch {
    return s;
  }
};

export const formatDate = (s: string): string => {
  if (!s) return '';
  try {
    const d = new Date(s);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return s;
  }
};

export const StatCard: React.FC<{
  label: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: string;
  trend?: 'up' | 'down' | 'flat';
}> = ({ label, value, subtitle, icon, color = COLORS.primary }) => {
  const theme = useTheme();
  return (
    <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: alpha(color, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const SectionLoading: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

export const SectionError: React.FC<{ message?: string }> = ({ message }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography color="error" variant="body2">
      無法載入資料{message ? `: ${message}` : ''}
    </Typography>
  </Box>
);

export const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography color="text.secondary" variant="body2">
      {message}
    </Typography>
  </Box>
);

/**
 * Cell color for heatmap — intensity based on % value.
 */
export const heatmapColor = (pct: number): string => {
  if (!pct || pct <= 0) return '#F8FAFC';
  if (pct < 5) return '#DBEAFE';
  if (pct < 10) return '#BFDBFE';
  if (pct < 20) return '#93C5FD';
  if (pct < 30) return '#60A5FA';
  if (pct < 50) return '#3B82F6';
  return '#1D4ED8';
};

export const heatmapText = (pct: number): string => (pct >= 20 ? '#FFFFFF' : '#1E293B');
